import React, { useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { 
  SET_RAW_DATA, 
  SET_DATA_DATES,
  SET_FILTERED_DATA,
  SET_FILTERED_LAST_DATA,
  SET_ORIGINAL_DATA_DATES
} from 'constants/actions';

import { 
  get_most_recent, 
  get_last_responses, 
  sort_historical_data, 
  get_dates, 
  filter_data 
} from 'reports/Audit/data_navigation/calculate_data';

import {
  checkPersonality,
  convert_personality_to_categories,
} from '../calculations';

// This component doesn't render anything visible - it just processes filter changes
const DateFilterProcessor = () => {
  const dispatch = useDispatch();
  
  const { 
    selectedDateFilter,
    get_culture_audit_reports,
    get_organizations,
    survey_version,
    get_survey_structure,
    nav_state,
    originalDates,
    raw_data
  } = useSelector((state) => ({
    selectedDateFilter: state.audit.selectedDateFilter,
    get_culture_audit_reports: state.debrief_schedule.culture_audit_reports,
    get_organizations: state.organizations,
    survey_version: state.audit.survey_version,
    get_survey_structure: state.surveystructure.survey_structure,
    nav_state: state.audit.nav,
    originalDates: state.audit.originalDates,
    raw_data: state.audit?.raw_data
  }), shallowEqual);

  // Check if we have custom data from NewAnalytics - don't overwrite it
  const hasCustomData = raw_data?.length > 0 && raw_data[0]?.datasetMetadata;

  // Initial loading effect to save the original dates
  useEffect(() => {
    console.log("=== DATE FILTER PROCESSOR INITIAL LOAD ===");
    console.log("Has custom data:", hasCustomData);
    
    // Don't interfere with custom data
    if (hasCustomData) {
      console.log("=== SKIPPING DATE FILTER PROCESSOR INITIAL LOAD - CUSTOM DATA EXISTS ===");
      return;
    }
    
    if (!originalDates && get_culture_audit_reports?.response?.length) {
      console.log("DateFilterProcessor: Saving original dates");
      
      let sorted_responses = get_culture_audit_reports.response.sort(
        (a, b) => b.id - a.id
      );

      console.log("DateFilterProcessor: Sorted responses:", sorted_responses);
      
      if (survey_version) {
        sorted_responses = sorted_responses.filter(
          (f) => f.question.sort_order === survey_version.sort_order
        );
      }
      
      if (checkPersonality(get_organizations)) {
        sorted_responses = convert_personality_to_categories(sorted_responses);
      }
      
      let most_recent = get_most_recent(sorted_responses);
      most_recent["last"] = get_last_responses(sorted_responses);
      
      if (sorted_responses.length > 0 && most_recent.responses) {
        // I just need a unqiue list of dates, remove any duplicates
        
        const dateArray = [...new Set(sorted_responses.map(item => new Date(item.date).toISOString()))]
        .map(dateStr => new Date(dateStr));

        
        // Store the original dates for later use
        dispatch({
          type: SET_ORIGINAL_DATA_DATES,
          payload: dateArray,
        });
        
      }
    }
  }, [get_culture_audit_reports?.response, survey_version, get_organizations, dispatch, originalDates, hasCustomData]);

  // Set up a dedicated effect for date filtering
  useEffect(() => {
    console.log("=== DATE FILTER PROCESSOR RUNNING ===");
    console.log("Has custom data:", hasCustomData);
    console.log("Custom data metadata:", raw_data?.[0]?.datasetMetadata);
    
    // Don't overwrite custom data from NewAnalytics
    if (hasCustomData) {
      console.log("=== SKIPPING DATE FILTER PROCESSOR - CUSTOM DATA EXISTS ===");
      return;
    }
    
    // Skip if we don't have data to filter
    if (!get_culture_audit_reports?.response?.length) {
      return;
    }
    
    // Start with the original unfiltered data
    let sorted_responses = get_culture_audit_reports.response.sort(
      (a, b) => b.id - a.id
    );

    
    // Apply survey version filter
    if (survey_version) {
      sorted_responses = sorted_responses.filter(
        (f) => f.question.sort_order === survey_version.sort_order
      );
    }
    
    // Apply personality conversion
    if (checkPersonality(get_organizations)) {
      sorted_responses = convert_personality_to_categories(sorted_responses);
    }
    
    // Apply date filter
    if (selectedDateFilter) {
      try {
        const filterDate = new Date(selectedDateFilter);
        
        if (!isNaN(filterDate.getTime())) {
          sorted_responses = sorted_responses.filter((item) => {
            try {
              if (!item.date) {
                console.warn("DateFilterProcessor: Item missing date property:", item);
                return true; // Include items without dates
              }
              
              const itemDate = new Date(item.date);
              const result = itemDate.getTime() === filterDate.getTime();
              return result;
            } catch (e) {
              console.error("DateFilterProcessor: Error parsing item date:", item.date, e);
              return true; // Include item if date parsing fails
            }
          });
        } else {
          console.error("DateFilterProcessor: Invalid filter date:", selectedDateFilter);
        }
      } catch (e) {
        console.error("DateFilterProcessor: Error applying date filter:", e);
      }
    }

    
    // Process filtered data
    let most_recent = get_most_recent(sorted_responses);
    most_recent["last"] = get_last_responses(sorted_responses);
    
    if (sorted_responses.length > 0 && most_recent.responses) {
      let historical_data = sort_historical_data(
        sorted_responses.reverse(),
        0
      );
      
      const rawData = [most_recent, ...historical_data];
      // Update raw data in Redux
      dispatch({
        type: SET_RAW_DATA,
        payload: rawData,
      });
      
      // Update dates (use originalDates instead of recalculating)
      // This ensures date options don't disappear when filtering
      if (originalDates) {
        dispatch({
          type: SET_DATA_DATES,
          payload: originalDates,
        });
      } else {
        const dateArray = get_dates(historical_data);
        dispatch({
          type: SET_DATA_DATES,
          payload: dateArray,
        });
      }
      
      
      // If we have navigation state, update the filtered data too
      if (nav_state && Object.keys(nav_state).length > 0 && get_survey_structure?.[0]) {
        const filtered_data = filter_data(
          rawData,
          nav_state,
          get_survey_structure[0]
        );
        
        dispatch({
          type: SET_FILTERED_DATA,
          payload: filtered_data.filter_data,
        });

        dispatch({
          type: SET_FILTERED_LAST_DATA,
          payload: filtered_data.filter_data_last,
        });
        
      }
    }
  }, [selectedDateFilter, survey_version, get_culture_audit_reports?.response, get_organizations, dispatch, nav_state, get_survey_structure, originalDates, hasCustomData]);

  // This component doesn't render anything visible
  return null;
};

export default DateFilterProcessor; 